import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { saveCustomContextLimits } from "../../utils/contextLimits";

const MODE_DEFAULTS = { simple: 6, smart: 8, genius: 12 };
const MAX_LIMIT = 50;

const SOURCE_DEFAULTS = {
  autoSearchQueue: false, autoSearchMainTopic: false, autoSearchTopics: false,
  autoSearchSubTopics: false, autoSearchScratchpad: false,
  autoSearchNotifications: false, autoSearchMeetings: false,
};

const TOGGLE_META = [
  { key: "autoSearchQueue", label: "Queue History", icon: "bi-inbox-fill", desc: "Search past queue conversations" },
  { key: "autoSearchMainTopic", label: "Main Topic", icon: "bi-house-door-fill", desc: "Search main topic session" },
  { key: "autoSearchTopics", label: "Topics", icon: "bi-chat-left-text-fill", desc: "Search topic sessions" },
  { key: "autoSearchSubTopics", label: "Sub Topics", icon: "bi-diagram-3-fill", desc: "Search sub topic sessions" },
  { key: "autoSearchScratchpad", label: "Scratch Pad", icon: "bi-journal-text", desc: "Search scratchpad notes" },
  { key: "autoSearchNotifications", label: "Notifications", icon: "bi-bell-fill", desc: "Search notification chats" },
  { key: "autoSearchMeetings", label: "Meetings", icon: "bi-camera-video-fill", desc: "Search meeting conversations" },
];

const MODE_META = [
  { id: "simple", label: "Simple", icon: "bi-lightning-charge-fill", color: "#f97316", desc: "Fast answers, daily tasks" },
  { id: "smart", label: "Smart", icon: "bi-stars", color: "#6366f1", desc: "Full power, all capabilities" },
  { id: "genius", label: "Genius", icon: "bi-gem", color: "#9333ea", desc: "Premium, deep personalization" },
];

const ContextSettingsModal = ({ show, onHide, userId, isDarkMode, onSave }) => {
  const [limits, setLimits] = useState({ ...MODE_DEFAULTS });
  const [sources, setSources] = useState({ ...SOURCE_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!show || !userId) return;
    const load = async () => {
      try {
        const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
        const res = await fetch(`${NEXT_API_URL}/api/user/profile?user_id=${userId}`);
        const data = await res.json();
        if (data.success && data.profile?.custom_context_limits) {
          const saved = data.profile.custom_context_limits;
          setLimits({
            simple: typeof saved.simple === "number" && saved.simple >= MODE_DEFAULTS.simple && saved.simple <= MAX_LIMIT ? saved.simple : MODE_DEFAULTS.simple,
            smart: typeof saved.smart === "number" && saved.smart >= MODE_DEFAULTS.smart && saved.smart <= MAX_LIMIT ? saved.smart : MODE_DEFAULTS.smart,
            genius: typeof saved.genius === "number" && saved.genius >= MODE_DEFAULTS.genius && saved.genius <= MAX_LIMIT ? saved.genius : MODE_DEFAULTS.genius,
          });
          const merged = {};
          for (const key of Object.keys(SOURCE_DEFAULTS)) {
            merged[key] = typeof saved[key] === "boolean" ? saved[key] : SOURCE_DEFAULTS[key];
          }
          setSources(merged);
        } else {
          setLimits({ ...MODE_DEFAULTS });
          setSources({ ...SOURCE_DEFAULTS });
        }
      } catch {
        setLimits({ ...MODE_DEFAULTS });
        setSources({ ...SOURCE_DEFAULTS });
      }
    };
    load();
  }, [show, userId]);

  const handleChange = (mode, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return;
    const clamped = Math.max(MODE_DEFAULTS[mode], Math.min(MAX_LIMIT, num));
    setLimits((prev) => ({ ...prev, [mode]: clamped }));
  };

  const handleToggle = (key) => {
    setSources((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleReset = () => {
    setLimits({ ...MODE_DEFAULTS });
    setSources({ ...SOURCE_DEFAULTS });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
      const payload = { enabled: true, ...limits, ...sources };
      const res = await fetch(`${NEXT_API_URL}/api/user/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, custom_context_limits: payload }),
      });
      const data = await res.json();
      if (data.success) {
        setToast({ type: "success", message: "Context limits saved!" });
        saveCustomContextLimits(payload);
        if (onSave) onSave(payload);
        setTimeout(() => { setToast(null); onHide(); }, 1200);
      } else {
        setToast({ type: "error", message: data.error || "Failed to save." });
        setTimeout(() => setToast(null), 3000);
      }
    } catch {
      setToast({ type: "error", message: "Something went wrong." });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="context-settings-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "16px", fontWeight: "600" }}>
          <i className="bi bi-sliders" style={{ marginRight: "8px", color: "var(--teal-bg, #0d9488)" }}></i>
          Context Limits Settings
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "18px 24px", maxHeight: "72vh", overflowY: "auto" }}>
        <p style={{ fontSize: "12px", color: "#888", margin: "0 0 12px 0" }}>
          Configure history limits and context sources. Min = default, Max = {MAX_LIMIT}.
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            padding: "10px 12px",
            borderRadius: "8px",
            background: isDarkMode ? "rgba(217, 119, 6, 0.1)" : "#fffbeb",
            border: `1px solid ${isDarkMode ? "rgba(217, 119, 6, 0.25)" : "#fde68a"}`,
            marginBottom: "16px",
            fontSize: "12px",
            color: isDarkMode ? "#fbbf24" : "#92400e",
            lineHeight: "1.5",
          }}
        >
          <i className="bi bi-info-circle-fill" style={{ fontSize: "14px", marginTop: "1px", flexShrink: 0 }}></i>
          <span>
            Increasing context limits and enabling extra sources will use more tokens
            and <strong>consume additional credits</strong> per request.
          </span>
        </div>

        {/* History Limits per Mode */}
        {MODE_META.map(({ id, label, icon, color, desc }) => (
          <div
            key={id}
            style={{
              marginBottom: "10px",
              padding: "12px 16px",
              borderRadius: "10px",
              border: `1px solid ${isDarkMode ? "var(--border-color, #333)" : "#e5e7eb"}`,
              background: isDarkMode ? "var(--card-bg, #1a1a2e)" : "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <i className={`bi ${icon}`} style={{ fontSize: "20px", color }}></i>
              <div>
                <span style={{ fontWeight: "600", fontSize: "14px" }}>{label}</span>
                <div style={{ fontSize: "11px", color: "#888" }}>{desc}</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "11px", color: "#888" }}>History:</span>
                <Form.Control
                  type="number"
                  size="sm"
                  min={MODE_DEFAULTS[id]}
                  max={MAX_LIMIT}
                  value={limits[id] ?? MODE_DEFAULTS[id]}
                  onChange={(e) => handleChange(id, e.target.value)}
                  style={{ width: "65px", fontSize: "14px", textAlign: "center", fontWeight: "600" }}
                />
                <span style={{ fontSize: "10px", color: "#aaa" }}>/{MAX_LIMIT}</span>
              </div>
              <span style={{ fontSize: "10px", color: "#666" }}>default: {MODE_DEFAULTS[id]}</span>
            </div>
          </div>
        ))}

        {/* Context Sources - Global */}
        <div style={{
          marginTop: "14px",
          padding: "14px 16px",
          borderRadius: "10px",
          border: `1px solid ${isDarkMode ? "var(--border-color, #333)" : "#e5e7eb"}`,
          background: isDarkMode ? "var(--card-bg, #1a1a2e)" : "#fafafa",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <i className="bi bi-plug-fill" style={{ fontSize: "16px", color: "var(--teal-bg, #0d9488)" }}></i>
            <span style={{ fontWeight: "600", fontSize: "14px" }}>Context Sources</span>
            <span style={{ fontSize: "10px", color: "#888", marginLeft: "4px" }}>applies to all modes</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
            {TOGGLE_META.map(({ key, label: tLabel, icon: tIcon, desc: tDesc }) => {
              const isOn = sources[key] ?? SOURCE_DEFAULTS[key];
              return (
                <div
                  key={key}
                  onClick={() => handleToggle(key)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px",
                    padding: "14px 8px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    background: isOn
                      ? isDarkMode ? "rgba(13, 148, 136, 0.15)" : "rgba(13, 148, 136, 0.08)"
                      : isDarkMode ? "rgba(255,255,255,0.03)" : "#fff",
                    border: `1.5px solid ${isOn
                      ? "var(--teal-bg, #0d9488)"
                      : isDarkMode ? "var(--border-color, #333)" : "#e5e7eb"}`,
                  }}
                >
                  <i
                    className={`bi ${tIcon}`}
                    style={{
                      fontSize: "22px",
                      color: isOn ? "var(--teal-bg, #0d9488)" : isDarkMode ? "#666" : "#aaa",
                      transition: "color 0.2s ease",
                    }}
                  ></i>
                  <span style={{
                    fontSize: "12px",
                    fontWeight: isOn ? "600" : "400",
                    color: isOn ? (isDarkMode ? "#5eead4" : "#0d9488") : (isDarkMode ? "#888" : "#666"),
                    textAlign: "center",
                    lineHeight: "1.3",
                  }}>
                    {tLabel}
                  </span>
                  <span style={{ fontSize: "10px", color: "#888", textAlign: "center", lineHeight: "1.2" }}>
                    {tDesc}
                  </span>
                  <div style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: isOn ? "#0d9488" : isDarkMode ? "#444" : "#ddd",
                    transition: "background 0.2s ease",
                  }}></div>
                </div>
              );
            })}
          </div>
        </div>

        {toast && (
          <div
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              background: toast.type === "success" ? "#0d9488" : "#dc2626",
              color: "#fff",
              fontSize: "12px",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            {toast.message}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outline-secondary" size="sm" onClick={handleReset}>
          Reset to Defaults
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          style={{
            background: "linear-gradient(135deg, #0d9488, #14b8a6)",
            border: "none",
            color: "#fff",
            padding: "6px 20px",
          }}
        >
          {saving ? <Spinner animation="border" size="sm" /> : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContextSettingsModal;
